import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Sparkles, 
  ArrowLeft,
  Save,
  Eye,
  Palette,
  Calendar,
  ToggleLeft,
  ToggleRight,
  Snowflake,
  PartyPopper,
  Flame,
  Volume2,
  VolumeX,
  Clock,
  Monitor,
  Smartphone,
  Users,
  Settings2,
  Check,
  X,
  Lock,
  Mail,
  KeyRound
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { Slider } from '../../components/ui/slider';
import { Input } from '../../components/ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Valid credentials
const VALID_CREDENTIALS = {
  email: 'care@netixfibernet.com',
  password: 'Netix@008331'
};

const AVAILABLE_THEMES = [
  {
    id: 'newyear2025',
    name: 'New Year 2026 🎆',
    icon: '🥳',
    description: 'Ring in 2026 with snow, fireworks & countdown!',
    colors: ['#FFD700', '#FF6B6B', '#4ECDC4'],
    elements: ['Snow', 'Confetti', 'Fireworks', 'Countdown', 'Banner']
  },
  {
    id: 'diwali',
    name: 'Diwali',
    icon: '🪔',
    description: 'Festival of Lights with diyas and sparkles',
    colors: ['#FF6B35', '#FFD700', '#E63946'],
    elements: ['Diyas', 'Sparklers', 'Rangoli']
  },
  {
    id: 'christmas',
    name: 'Christmas',
    icon: '🎄',
    description: 'Festive Christmas with snowflakes and bells',
    colors: ['#C41E3A', '#165B33', '#FFFFFF'],
    elements: ['Snowflakes', 'Bells', 'Santa']
  }
];

const APPLY_TO_OPTIONS = [
  { id: 'customer', label: 'Customer Portal', icon: Users },
  { id: 'admin', label: 'Admin Dashboard', icon: Settings2 },
  { id: 'monitoring', label: 'Monitoring Dashboard', icon: Monitor },
  { id: 'fieldstaff', label: 'Field Staff App', icon: Smartphone }
];

const FestivalThemeAdmin = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  
  const [settings, setSettings] = useState({
    theme_enabled: false,
    active_theme: 'none',
    intensity: 'medium',
    animation_speed: 'normal',
    apply_to: ['customer', 'admin', 'monitoring', 'fieldstaff'],
    show_banner: true,
    show_confetti: true,
    show_fireworks: true,
    sound_enabled: false,
    scheduled_start: '',
    scheduled_end: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);

  // Check if already authenticated
  useEffect(() => {
    const authStatus = sessionStorage.getItem('festivalThemeAuth');
    if (authStatus === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchSettings();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated]);

  const handleLogin = (e) => {
    e.preventDefault();
    setIsLoggingIn(true);
    setLoginError('');
    
    // Simulate a small delay for better UX
    setTimeout(() => {
      if (loginEmail === VALID_CREDENTIALS.email && loginPassword === VALID_CREDENTIALS.password) {
        setIsAuthenticated(true);
        sessionStorage.setItem('festivalThemeAuth', 'true');
        toast.success('Login successful!');
      } else {
        setLoginError('Invalid email or password');
        toast.error('Invalid credentials');
      }
      setIsLoggingIn(false);
    }, 500);
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem('festivalThemeAuth');
    toast.info('Logged out');
  };

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('adminToken') || localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/admin/festival-themes/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = response.data;
      setSettings({
        theme_enabled: data.theme_enabled || false,
        active_theme: data.active_theme || 'none',
        intensity: data.intensity || 'medium',
        animation_speed: data.animation_speed || 'normal',
        apply_to: data.apply_to || ['customer', 'admin', 'monitoring', 'fieldstaff'],
        show_banner: data.show_banner !== false,
        show_confetti: data.show_confetti !== false,
        show_fireworks: data.show_fireworks !== false,
        sound_enabled: data.sound_enabled || false,
        scheduled_start: data.scheduled_start ? data.scheduled_start.slice(0, 16) : '',
        scheduled_end: data.scheduled_end ? data.scheduled_end.slice(0, 16) : ''
      });
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Failed to load theme settings');
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('adminToken') || localStorage.getItem('token');
      const payload = {
        ...settings,
        scheduled_start: settings.scheduled_start || null,
        scheduled_end: settings.scheduled_end || null
      };
      
      await axios.put(`${API_URL}/api/admin/festival-themes/settings`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Theme settings saved successfully!');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const toggleApplyTo = (id) => {
    setSettings(prev => ({
      ...prev,
      apply_to: prev.apply_to.includes(id)
        ? prev.apply_to.filter(item => item !== id)
        : [...prev.apply_to, id]
    }));
  };

  const selectTheme = (themeId) => {
    setSettings(prev => ({
      ...prev,
      active_theme: themeId,
      theme_enabled: themeId !== 'none'
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-white/10 border-white/20 backdrop-blur-xl shadow-2xl">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center mb-4 shadow-lg">
              <Sparkles className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-2xl font-bold text-white">Festival Theme Manager</CardTitle>
            <CardDescription className="text-purple-200">
              Sign in to manage festive themes
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-4">
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-white flex items-center gap-2">
                  <Mail className="w-4 h-4" /> Email
                </Label>
                <Input
                  type="email"
                  placeholder="Enter your email"
                  value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)}
                  className="bg-white/10 border-white/20 text-white placeholder:text-gray-400 focus:border-purple-400"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white flex items-center gap-2">
                  <KeyRound className="w-4 h-4" /> Password
                </Label>
                <Input
                  type="password"
                  placeholder="Enter your password"
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  className="bg-white/10 border-white/20 text-white placeholder:text-gray-400 focus:border-purple-400"
                  required
                />
              </div>
              {loginError && (
                <div className="p-3 bg-red-500/20 border border-red-500/40 rounded-lg text-red-300 text-sm flex items-center gap-2">
                  <X className="w-4 h-4" /> {loginError}
                </div>
              )}
              <Button
                type="submit"
                disabled={isLoggingIn}
                className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-semibold py-2.5"
              >
                {isLoggingIn ? (
                  <span className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Signing in...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Lock className="w-4 h-4" /> Sign In
                  </span>
                )}
              </Button>
            </form>
            <div className="mt-6 pt-4 border-t border-white/10">
              <button
                onClick={() => navigate(-1)}
                className="w-full text-purple-300 hover:text-white text-sm flex items-center justify-center gap-2 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" /> Back to previous page
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-white" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                <Sparkles className="w-6 h-6 text-yellow-400" />
                Festival Theme Manager
              </h1>
              <p className="text-purple-200 text-sm">Configure festive themes across all dashboards</p>
            </div>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => setPreviewMode(!previewMode)}
              className="border-purple-400 text-purple-200 hover:bg-purple-500/20"
            >
              <Eye className="w-4 h-4 mr-2" />
              Preview
            </Button>
            <Button
              onClick={saveSettings}
              disabled={saving}
              className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>

        {/* Master Toggle */}
        <Card className="bg-white/10 border-white/20 backdrop-blur-lg">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                {settings.theme_enabled ? (
                  <ToggleRight className="w-10 h-10 text-green-400" />
                ) : (
                  <ToggleLeft className="w-10 h-10 text-gray-400" />
                )}
                <div>
                  <h3 className="text-lg font-semibold text-white">Festival Theme</h3>
                  <p className="text-purple-200 text-sm">
                    {settings.theme_enabled ? 'Theme is active across selected dashboards' : 'Theme is currently disabled'}
                  </p>
                </div>
              </div>
              <Switch
                checked={settings.theme_enabled}
                onCheckedChange={(checked) => setSettings(prev => ({ ...prev, theme_enabled: checked }))}
              />
            </div>
          </CardContent>
        </Card>

        {/* Theme Selection */}
        <Card className="bg-white/10 border-white/20 backdrop-blur-lg">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Palette className="w-5 h-5 text-purple-400" />
              Select Theme
            </CardTitle>
            <CardDescription className="text-purple-200">Choose a festive theme for your application</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {AVAILABLE_THEMES.map((theme) => (
                <div
                  key={theme.id}
                  onClick={() => selectTheme(theme.id)}
                  className={`relative p-5 rounded-xl cursor-pointer transition-all duration-300 border-2 ${
                    settings.active_theme === theme.id
                      ? 'border-purple-400 bg-purple-500/30 scale-105'
                      : 'border-white/20 bg-white/5 hover:bg-white/10 hover:border-white/40'
                  }`}
                >
                  {settings.active_theme === theme.id && (
                    <div className="absolute top-2 right-2 w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                  )}
                  <div className="text-4xl mb-3">{theme.icon}</div>
                  <h4 className="text-lg font-semibold text-white mb-1">{theme.name}</h4>
                  <p className="text-purple-200 text-sm mb-3">{theme.description}</p>
                  <div className="flex gap-2 mb-3">
                    {theme.colors.map((color, idx) => (
                      <div
                        key={idx}
                        className="w-6 h-6 rounded-full border-2 border-white/30"
                        style={{ backgroundColor: color }}
                      />
                    ))}
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {theme.elements.map((el, idx) => (
                      <span
                        key={idx}
                        className="text-xs px-2 py-1 bg-white/10 rounded-full text-purple-200"
                      >
                        {el}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Effects Controls */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Visual Effects */}
          <Card className="bg-white/10 border-white/20 backdrop-blur-lg">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <PartyPopper className="w-5 h-5 text-pink-400" />
                Visual Effects
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Snowflake className="w-5 h-5 text-blue-300" />
                  <Label className="text-white">Snow / Confetti</Label>
                </div>
                <Switch
                  checked={settings.show_confetti}
                  onCheckedChange={(checked) => setSettings(prev => ({ ...prev, show_confetti: checked }))}
                />
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Flame className="w-5 h-5 text-orange-400" />
                  <Label className="text-white">Fireworks</Label>
                </div>
                <Switch
                  checked={settings.show_fireworks}
                  onCheckedChange={(checked) => setSettings(prev => ({ ...prev, show_fireworks: checked }))}
                />
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-yellow-400" />
                  <Label className="text-white">Banner</Label>
                </div>
                <Switch
                  checked={settings.show_banner}
                  onCheckedChange={(checked) => setSettings(prev => ({ ...prev, show_banner: checked }))}
                />
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {settings.sound_enabled ? (
                    <Volume2 className="w-5 h-5 text-green-400" />
                  ) : (
                    <VolumeX className="w-5 h-5 text-gray-400" />
                  )}
                  <Label className="text-white">Sound Effects</Label>
                </div>
                <Switch
                  checked={settings.sound_enabled}
                  onCheckedChange={(checked) => setSettings(prev => ({ ...prev, sound_enabled: checked }))}
                />
              </div>
              
              {/* Intensity Slider */}
              <div className="pt-4 border-t border-white/10">
                <Label className="text-white mb-3 block">Effect Intensity</Label>
                <div className="flex gap-3">
                  {['subtle', 'medium', 'heavy'].map((level) => (
                    <button
                      key={level}
                      onClick={() => setSettings(prev => ({ ...prev, intensity: level }))}
                      className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all ${
                        settings.intensity === level
                          ? 'bg-purple-500 text-white'
                          : 'bg-white/10 text-purple-200 hover:bg-white/20'
                      }`}
                    >
                      {level.charAt(0).toUpperCase() + level.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Apply To */}
          <Card className="bg-white/10 border-white/20 backdrop-blur-lg">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Monitor className="w-5 h-5 text-cyan-400" />
                Apply Theme To
              </CardTitle>
              <CardDescription className="text-purple-200">Select which dashboards show the theme</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {APPLY_TO_OPTIONS.map((option) => (
                <div
                  key={option.id}
                  onClick={() => toggleApplyTo(option.id)}
                  className={`flex items-center justify-between p-4 rounded-lg cursor-pointer transition-all ${
                    settings.apply_to.includes(option.id)
                      ? 'bg-purple-500/30 border border-purple-400'
                      : 'bg-white/5 border border-white/10 hover:bg-white/10'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <option.icon className={`w-5 h-5 ${
                      settings.apply_to.includes(option.id) ? 'text-purple-300' : 'text-gray-400'
                    }`} />
                    <span className="text-white font-medium">{option.label}</span>
                  </div>
                  {settings.apply_to.includes(option.id) ? (
                    <Check className="w-5 h-5 text-green-400" />
                  ) : (
                    <X className="w-5 h-5 text-gray-500" />
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        </div>

        {/* Schedule */}
        <Card className="bg-white/10 border-white/20 backdrop-blur-lg">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Clock className="w-5 h-5 text-green-400" />
              Schedule (Optional)
            </CardTitle>
            <CardDescription className="text-purple-200">
              Set start and end dates to automatically enable/disable the theme
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label className="text-white mb-2 block">Start Date & Time</Label>
                <input
                  type="datetime-local"
                  value={settings.scheduled_start}
                  onChange={(e) => setSettings(prev => ({ ...prev, scheduled_start: e.target.value }))}
                  className="w-full p-3 rounded-lg bg-white/10 border border-white/20 text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <div>
                <Label className="text-white mb-2 block">End Date & Time</Label>
                <input
                  type="datetime-local"
                  value={settings.scheduled_end}
                  onChange={(e) => setSettings(prev => ({ ...prev, scheduled_end: e.target.value }))}
                  className="w-full p-3 rounded-lg bg-white/10 border border-white/20 text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
            </div>
            {settings.scheduled_start && settings.scheduled_end && (
              <div className="mt-4 p-3 bg-green-500/20 border border-green-500/40 rounded-lg">
                <p className="text-green-300 text-sm">
                  ✓ Theme will be active from {new Date(settings.scheduled_start).toLocaleString()} to {new Date(settings.scheduled_end).toLocaleString()}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default FestivalThemeAdmin;
